import * as Sentry from "@sentry/react"
import { RegistrarLogErro } from "../../services/clientefiel"
import { removeAcento } from "../../services/functionsGerais"
import {
    COR_STATUS_ACEITO,
    COR_STATUS_EM_ROTA,
    COR_STATUS_FINALIZADO,
    COR_STATUS_PREPARO,
    COR_STATUS_PRONTO,
} from "../../utils/coresStatusPedido"

// Status escondidos do painel — os mesmos que o backend oculta na listagem allday
// (FALHA_PAGAMENTO -5, AGUARDANDO_PAGAMENTO 77, CADASTRADO 99).
const STATUS_OCULTOS_DO_PAINEL = [-5, 77, 99]
export const statusOcultoDoPainel = status => STATUS_OCULTOS_DO_PAINEL.includes(status)

// Só avisa o Sentry na 1ª vez que um status desconhecido aparece nesta sessão (evita
// inundar caso um tipo novo passe a chegar em massa).
let jaReportouStatusDesconhecido = false

// Tempo sem avançar a partir do qual um pedido nos estágios iniciais é considerado
// TRAVADO nos modos automáticos. Fonte ÚNICA do limite: o card (colunaDoPedido) e o
// banner do Recebedor usam o mesmo valor, para o aviso e a ação aparecerem juntos.
export const LIMITE_TRAVADO_MS = 40000

// Pedido que já passou do limite sem avançar. Sem data de criação, considera travado
// (mostra por segurança — melhor um card a mais do que um pedido invisível).
export const pedidoTravado = (pedido, limiteMs = LIMITE_TRAVADO_MS) => {
    const t = pedido?.createdAt ? new Date(pedido.createdAt).getTime() : null
    if (t == null || Number.isNaN(t)) return true
    return Date.now() - t >= limiteMs
}

// Fonte ÚNICA da decisão "em qual coluna o pedido aparece", a partir do status. É o que
// substitui as várias arrays fatiadas do store: a UI mantém UMA lista e cada coluna
// filtra por esta função.
//
// `opcoes` = { prepararPedidoAutomatico, autoAceite, exibirAguardandoEntregador } (flags calculados pelo caller, para
// esta função não precisar importar o store — evita import circular).
//
//   1 · Aceito         → 0, 1, 2 (Aguardando ou Aceito)
//   2 · Em Preparação  → 7, 10 (Atualizando Status) e qualquer positivo desconhecido
//   5 · Aguardando Entregador/Retirada → 6 (PRONTO), só com `exibirAguardandoEntregador`
//   3 · Em Entrega     → 3, e 6 quando a raia 5 está desligada
//   4 · Finalizado     → 4, 5 (PAGO), 9 (PARCIALMENTE_PAGO) e qualquer negativo
//   null               → não exibido
//
// A raia 5 recebeu o número 5 (e não foi encaixada renumerando as demais) para não mexer
// nas regras já existentes por índice; na tela ela é desenhada entre a 2 e a 3.
//
// Modos AUTOMÁTICOS não pedem a ação manual na coluna (ela é feita sozinha):
//   - preparo automático → coluna 1 OCULTA (0,1,2 não aparecem); com aceite MANUAL o 0
//     continua na coluna 1 para o operador aceitar (o preparo vem sozinho em seguida);
//   - aceite automático  → status 0 (Aguardando) OCULTO (é aceito sozinho); 1,2 aparecem.
// Em ambos, um pedido que TRAVAR nesses estágios surge no banner (ver index.js), não na
// coluna. No modo manual, 0,1,2 aparecem normalmente com os botões Aceitar/Preparar.
/** Índice da raia "Aguardando Entregador/Retirada" (status PRONTO = 6). */
export const COLUNA_AGUARDANDO_ENTREGADOR = 5

// Cor de cada raia (cores originais do Recebedor) — os marcadores da Roteirização usam
// as mesmas (utils/coresStatusPedido).
export const corDaRaia = indexLista => {
    switch (indexLista) {
        case 1:
            return COR_STATUS_ACEITO
        case 2:
            return COR_STATUS_PREPARO
        case COLUNA_AGUARDANDO_ENTREGADOR:
            return COR_STATUS_PRONTO
        case 3:
            return COR_STATUS_EM_ROTA
        case 4:
            return COR_STATUS_FINALIZADO
        default:
            return "#fff"
    }
}

export const colunaDoPedido = (pedido, opcoes) => {
    const { prepararPedidoAutomatico, autoAceite, idsComFalha, exibirAguardandoEntregador } =
        opcoes || {}
    const s = pedido?.status
    if (statusOcultoDoPainel(s)) return null
    if (s === 6) return exibirAguardandoEntregador ? COLUNA_AGUARDANDO_ENTREGADOR : 3
    if (s === 3) return 3
    if (s === 4 || s === 5 || s === 9) return 4
    if (s < 0) return 4 // negativo desconhecido → Finalizado (cancelamento-like)
    // 7 = EM_PRODUCAO; 10 = AGUARDANDO_ATUALIZAR_STATUS (transitório, conhecido). Ambos
    // ficam em "Em Preparação". Tratar o 10 aqui EXPLICITAMENTE evita que ele caia no
    // branch de "positivo desconhecido" abaixo — que o exibia igual, mas disparava um
    // Sentry.captureMessage("status desconhecido") indevido para um status já mapeado.
    if (s === 7 || s === 10) return 2
    if (s === 0 || s === 1 || s === 2) {
        // Os modos automáticos escondem os estágios iniciais — mas SÓ enquanto a rotina
        // ainda tem chance de rodar. Passado LIMITE_TRAVADO_MS sem avançar, o pedido VOLTA
        // para a coluna 1, com o botão Aceitar/Preparar: antes ele só existia no banner do
        // topo (e apenas na tela do Recebedor), então uma falha do aceite automático o
        // deixava sem card nenhum até o backend expirá-lo.
        // Falha registrada no aceite/preparo desta estação: aparece NA HORA, sem esperar
        // o limite de travado. É a regra "pedido com erro sempre aparece no painel".
        const falhouAcao = !!idsComFalha && idsComFalha.includes(pedido?.id)
        if (!falhouAcao && !pedidoTravado(pedido)) {
            // Preparo automático: 1/2 são preparados sozinhos → ocultos. O 0 só some junto
            // quando o aceite também é automático; com aceite MANUAL ele fica na coluna 1
            // com o botão Aceitar (e depois do clique segue sozinho para o preparo).
            if (prepararPedidoAutomatico && (autoAceite || s !== 0)) return null
            if (autoAceite && s === 0) return null // aceite automático: é feito sozinho
        }
        return 1
    }
    // Positivo desconhecido (tipo novo do backend): fica VISÍVEL em Em Preparação em vez
    // de sumir, e avisa o Sentry uma vez para mapearmos o tipo depois.
    if (!jaReportouStatusDesconhecido) {
        jaReportouStatusDesconhecido = true
        try {
            Sentry.captureMessage("Status de pedido desconhecido no painel", {
                level: "warning",
                tags: { area: "recebedor", motivo: "status-desconhecido" },
                extra: { status: s, pedidoId: pedido?.id ?? null },
            })
        } catch {
            /* best-effort */
        }
    }
    return 2
}

export const verificarPrintQRCode = pedido => {
    const qrCode = { descricao: "", texto: "" }

    if (pedido && pedido.payments) {
        if (pedido.payments.codigoQrCodePix) {
            qrCode.texto = pedido.payments.codigoQrCodePix
        }
        if (pedido.payments.tipoChavePix && pedido.payments.chavePix) {
            qrCode.descricao = `PIX ${pedido.payments.chavePix}: ${pedido.payments.tipoChavePix}`
        }
    }

    return qrCode
}

export const procurarPedido = (e, pedidos, popUp = true) => {
    const pesquisa = removeAcento(
        e.target.value ?? document.getElementById("procurar-pedido-alternativo").value
    )
    try {
        pedidos.forEach(pedido => {
            const item = document.getElementById(`pedido-${pedido.id}`)
            const divider = document.getElementById(`divider-${pedido.id}`)

            const pedidoDadosString = removeAcento(
                pedido.id.toString() + (pedido.customer?.name ?? "") + pedido.customer?.phone
            )

            if (!pesquisa || pedidoDadosString.includes(pesquisa)) {
                if (item?.style) item.style.display = ""
                if (divider?.style) divider.style.display = ""
            } else {
                if (item?.style) item.style.display = "none"
                if (divider?.style) divider.style.display = "none"
            }
        })
    } catch (error) {
        RegistrarLogErro(error, "procurarPedido", pesquisa)
    }
}

export const totalArray = (tipo, arrayQuarto) => {
    try {
        //tipo 1 = verde
        //tipo 0 = vermelho
        var totalPriceP = 0.0
        var totalPriceN = 0.0

        arrayQuarto.forEach(pedido => {
            if (pedido.status > 0) {
                totalPriceP += pedido.totalPrice
            } else {
                totalPriceN += pedido.totalPrice
            }
        })

        if (tipo === 0) {
            return totalPriceN
        } else {
            return totalPriceP
        }
    } catch (error) {
        RegistrarLogErro(error, "totalArray", { tipo, arrayQuarto })
    }
}
